import { StylishNewButton } from '../../DesignSystems/New/StylishNewButton';
import React from 'react';
import { ResourceSet } from '../hooks/resourceSetHooks';
import { getLatLongFromGeometry } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/getLatLongFromGeometry';
import { useResourceSites, ResourceSite } from '../hooks/resourceSiteHooks';
import { useResourceRelationships } from 'hooks/resources/useResourceRelationships';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { useSelector } from 'react-redux';
import { Badge, Row, Col } from 'react-bootstrap';
import { FaMapMarkerAlt } from 'react-icons/fa';
import ExternalResourceInfoIcon from '@/components/IAP/Resources/ExternalResourceInfoIcon';

// Type for RootState
interface RootState {
  app: {
    currentIncident: { id: string; name: string };
  };
}

const ResourceSetCard: React.FC<{
  row: ResourceSet;
  selectCallback: (set: ResourceSet) => void;
}> = ({ row, selectCallback }) => {
  const { data: resourceSites } = useResourceSites();
  const { data: relationships = [] } = useResourceRelationships(
    row.resource_set_id,
    'resource'
  );
  const currentIncident = useSelector(
    (state: RootState) => state.app.currentIncident
  );
  const { data: rosterData = [] } = useGetRosterForIncident(
    currentIncident?.id
  );

  // Find resource site name
  const resourceSite = resourceSites?.find(
    (site) => site.id === row.resource_site_id
  );

  // Find assigned member names
  const assignedMembers = relationships
    .map((rel) => {
      const member = rosterData.find((m) => m.incidentMemberId === rel.id);
      return member
        ? member.emailAddress || member.userName || 'Unknown'
        : null;
    })
    .filter(Boolean);

  // Helper function to format location display
  const formatLocation = (location: any, siteId?: string) => {
    if (!location && !siteId) return 'Not specified';

    // If we have a site ID, try to find the site name
    if (siteId && resourceSites) {
      const site = resourceSites.find((s: ResourceSite) => s.id === siteId);
      if (site) return site.name;
    }

    // If we have location coordinates, display them
    const latitude = location?.latitude ?? location?.lat;
    const longitude = location?.longitude ?? location?.lng;
    if (latitude != null || longitude != null) {
      return `${latitude?.toFixed(4)}, ${longitude?.toFixed(4)}`;
    }

    return 'Not specified';
  };

  return (
    <>
      <ExternalResourceInfoIcon resourceSet={row} />
      <div className="mb-4">
        <h6 className="mb-2">Resource Name:</h6>
        <p className="m-0">{row.name}</p>
      </div>
      {row.description && (
        <div className="mb-4">
          <h6 className="mb-2">Description:</h6>
          <p className="m-0">{row.description}</p>
        </div>
      )}
      <div className="mb-4">
        <h6 className="mb-2">Kind:</h6>
        <p className="m-0">{row.kind}</p>
      </div>
      <div className="mb-4">
        <h6 className="mb-2">Type:</h6>
        <p className="m-0">{row.type || 'General'}</p>
      </div>
      <div className="mb-4">
        <h6 className="mb-2">Resource Site:</h6>
        <p className="m-0">{resourceSite?.name || 'Not assigned'}</p>
      </div>
      <div className="mb-4">
        <h6 className="mb-2">Quantity:</h6>
        <p className="m-0">{row.quantity}</p>
      </div>
      <div className="mb-4">
        <h6 className="mb-2">Assigned Members:</h6>
        {assignedMembers.length > 0 ? (
          <div>
            {assignedMembers.map((member, index) => (
              <Badge key={index} bg="secondary" className="me-2 mb-1">
                {member}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-muted m-0">No members assigned</p>
        )}
      </div>
      {row.date_time_ordered && (
        <div className="mb-4">
          <h6 className="mb-2">Date Ordered:</h6>
          <p className="m-0">
            {new Date(row.date_time_ordered).toLocaleString()}
          </p>
        </div>
      )}
      {row.eta && (
        <div className="mb-4">
          <h6 className="mb-2">ETA:</h6>
          <p className="m-0">{new Date(row.eta).toLocaleString()}</p>
        </div>
      )}

      {/* Status and Location Information Section at the bottom */}
      <div className="border-top mt-3 pt-3">
        <Row className="mb-3">
          <Col xs={12}>
            <h6 className="text-primary mb-3">
              <FaMapMarkerAlt className="me-2" />
              Status & Location Information
            </h6>
          </Col>
        </Row>

        {/* Status */}
        <Row className="mb-3">
          <Col xs={12} md={3}>
            <strong>Status:</strong>
          </Col>
          <Col xs={12} md={9}>
            <Badge
              bg={
                row.status === 'Available'
                  ? 'success'
                  : row.status === 'In Transit'
                    ? 'warning'
                    : row.status === 'Ordered'
                      ? 'info'
                      : row.status === 'Onsite'
                        ? 'primary'
                        : 'secondary'
              }
              className="px-3 py-2"
            >
              {row.status}
            </Badge>
          </Col>
        </Row>

        {/* Location Fields - Show based on status */}
        {row.status === 'In Transit' || row.status === 'Ordered' ? (
          <>
            {/* Source Location */}
            <Row className="mb-2">
              <Col xs={12} md={3}>
                <strong>Source Location:</strong>
              </Col>
              <Col xs={12} md={9}>
                {formatLocation(
                  row.source_location,
                  row.source_resource_site_id
                )}
              </Col>
            </Row>

            {/* Current Location */}
            <Row className="mb-2">
              <Col xs={12} md={3}>
                <strong>Current Location:</strong>
              </Col>
              <Col xs={12} md={9}>
                {formatLocation(
                  getLatLongFromGeometry(row.geometry),
                  row.resource_site_id
                )}
              </Col>
            </Row>

            {/* Destination Location */}
            <Row className="mb-2">
              <Col xs={12} md={3}>
                <strong>Destination Location:</strong>
              </Col>
              <Col xs={12} md={9}>
                {formatLocation(
                  row.destination_location,
                  row.destination_resource_site_id
                )}
              </Col>
            </Row>
          </>
        ) : (
          /* For Onsite or Available status, just show current location */
          <Row className="mb-2">
            <Col xs={12} md={3}>
              <strong>Current Location:</strong>
            </Col>
            <Col xs={12} md={9}>
              {formatLocation(
                getLatLongFromGeometry(row.geometry),
                row.resource_site_id
              )}
            </Col>
          </Row>
        )}
      </div>

      <div className="mt-3">
        <StylishNewButton
          className="button button--link"
          onClick={() => selectCallback(row)}
        >
          Edit Resource
        </StylishNewButton>
      </div>
    </>
  );
};
export default ResourceSetCard;
